// ── A/R period comparison: MoM, QoQ, YoY — on the last value or the average ─
//
// Reads the certificate history (getBorrowingBases rows) and compares each A/R
// line with the month, quarter and year before. Calendar periods, by the
// certificate's as-of date, so a weekly reporter and a monthly reporter are
// compared on the same clock.
//
//   LAST VALUE   the latest certificate against
//                  MoM  the last certificate of the previous calendar month
//                  QoQ  the last certificate of the previous calendar quarter
//                  YoY  the last certificate of the same month a year earlier
//   AVERAGE      the average of every certificate in the period against
//                  MoM  this month so far vs the whole previous month
//                  QoQ  this quarter so far vs the whole previous quarter
//                  YoY  this year to date vs the same months a year earlier
//
// Dollar lines compare as a % change; share lines (% over 90 days) compare in
// percentage POINTS. A comparison with nothing on the other side is null —
// shown as a dash, never as zero.

const num = v => (v == null || v === "" ? null : (isFinite(parseFloat(v)) ? parseFloat(v) : null));
const d10 = d => String(d || "").slice(0, 10);

const bucketsOf = r => ["ar_inv_0_30", "ar_inv_31_60", "ar_inv_61_90", "ar_inv_91_120", "ar_inv_over120"].map(k => num(r[k]) || 0);
const agedTotal = r => bucketsOf(r).reduce((a, x) => a + x, 0);

// worse: which direction is bad news — "up" colours a rise red, "down" a fall.
export const AR_MEASURES = [
  { key: "ending",   label: "Ending A/R",        kind: "usd", get: r => num(r.ar_ending_balance) },
  { key: "eligible", label: "Eligible A/R",      kind: "usd", get: r => num(r.ar_total_eligible), worse: "down" },
  { key: "inel",     label: "Ineligible A/R",    kind: "usd", get: r => num(r.ar_total_ineligible), worse: "up" },
  { key: "net",      label: "Net available A/R", kind: "usd", get: r => num(r.ar_net_available), worse: "down" },
  { key: "b0",       label: "0–30",    kind: "usd", indent: true, get: r => (agedTotal(r) ? bucketsOf(r)[0] : null) },
  { key: "b31",      label: "31–60",   kind: "usd", indent: true, get: r => (agedTotal(r) ? bucketsOf(r)[1] : null) },
  { key: "b61",      label: "61–90",   kind: "usd", indent: true, get: r => (agedTotal(r) ? bucketsOf(r)[2] : null), worse: "up" },
  { key: "b91",      label: "91–120",  kind: "usd", indent: true, get: r => (agedTotal(r) ? bucketsOf(r)[3] : null), worse: "up" },
  { key: "b120",     label: "120+",    kind: "usd", indent: true, get: r => (agedTotal(r) ? bucketsOf(r)[4] : null), worse: "up" },
  { key: "o90",      label: "% over 90 days", kind: "pct", get: r => { const t = agedTotal(r), b = bucketsOf(r); return t ? (b[3] + b[4]) / t : null; }, worse: "up" },
];

const ym = d => d.slice(0, 7);
const qk = d => d.slice(0, 4) + "Q" + (Math.floor((parseInt(d.slice(5, 7), 10) - 1) / 3) + 1);
const prevYm = s => { let y = +s.slice(0, 4), m = +s.slice(5, 7) - 1; if (m === 0) { y--; m = 12; } return y + "-" + String(m).padStart(2, "0"); };
const prevQk = s => { let y = +s.slice(0, 4), q = +s.slice(5) - 1; if (q === 0) { y--; q = 4; } return y + "Q" + q; };
const lastYearYm = s => (+s.slice(0, 4) - 1) + s.slice(4);

const avg = xs => { const v = xs.filter(x => x != null); return v.length ? v.reduce((a, x) => a + x, 0) / v.length : null; };

/**
 * @param hist   getBorrowingBases rows, any order
 * @param basis  "last" | "avg"
 * @returns {{asof, labels:{mom,qoq,yoy}, rows:Array<{...measure, cur, mom, qoq, yoy}>}}
 *          each of mom/qoq/yoy: { prior, chg } (chg: fraction for usd, points-as-fraction for pct)
 */
export function arPeriodCompare(hist, basis = "last") {
  const rows = (hist || []).map(r => ({ r, d: d10(r.asofdate), id: Number(r.id) || 0 }))
    .filter(x => x.d)
    .sort((a, b) => (a.d === b.d ? a.id - b.id : a.d < b.d ? -1 : 1));   // oldest → newest
  if (!rows.length) return { asof: null, labels: {}, rows: [] };

  const L = rows[rows.length - 1];
  const M = ym(L.d), Q = qk(L.d), Y = L.d.slice(0, 4);
  const lastIn = pred => { for (let i = rows.length - 1; i >= 0; i--) if (pred(rows[i].d)) return rows[i]; return null; };
  const allIn = pred => rows.filter(x => pred(x.d));

  if (basis === "avg") {
    const curM = allIn(d => ym(d) === M), prvM = allIn(d => ym(d) === prevYm(M));
    const curQ = allIn(d => qk(d) === Q), prvQ = allIn(d => qk(d) === prevQk(Q));
    const ytd = allIn(d => d.slice(0, 4) === Y && ym(d) <= M);
    const lytd = allIn(d => d.slice(0, 4) === String(+Y - 1) && ym(d) <= lastYearYm(M));
    const labels = { cur: "This month avg", mom: "Prior month avg", qoq: "Prior quarter avg", yoy: "Same period last year avg",
               curQ: "Quarter to date", curY: "Year to date" };
    const f = (m, set) => (set && set.length ? avg(set.map(x => m.get(x.r))) : null);
    return {
      asof: L.d, basis, labels,
      rows: AR_MEASURES.map(m => {
        const c = f(m, curM), cq = f(m, curQ), cy = f(m, ytd);
        const one = (now, prior) => ({ prior, now, chg: chg(m, now, prior) });
        return { ...m, cur: c, mom: one(c, f(m, prvM)), qoq: one(cq, f(m, prvQ)), yoy: one(cy, f(m, lytd)) };
      }),
    };
  }

  const cur = L;
  const mom = lastIn(d => ym(d) === prevYm(M));
  const qoq = lastIn(d => qk(d) === prevQk(Q));
  const yoy = lastIn(d => ym(d) === lastYearYm(M));
  const labels = { cur: "Latest", mom: mom ? mom.d : null, qoq: qoq ? qoq.d : null, yoy: yoy ? yoy.d : null };
  return {
    asof: L.d, basis, labels,
    rows: AR_MEASURES.map(m => {
      const c = m.get(cur.r);
      const one = p => { const prior = p ? m.get(p.r) : null; return { prior, now: c, chg: chg(m, c, prior) }; };
      return { ...m, cur: c, mom: one(mom), qoq: one(qoq), yoy: one(yoy) };
    }),
  };
}

function chg(m, now, prior) {
  if (now == null || prior == null) return null;
  if (m.kind === "pct") return now - prior;                 // points
  if (prior === 0) return null;                             // no base to grow from
  return (now - prior) / Math.abs(prior);
}
